import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  Box, Typography, Card, CardContent, CircularProgress,
  Chip, Avatar, Divider, LinearProgress
} from '@mui/material'
import { EmojiEvents, CheckCircle, Cancel } from '@mui/icons-material'
import { useParams, useLocation, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useEventQuizSocket, playSound } from '../../hooks/useEventQuizSocket'
import QuestionDisplay from '../../components/EventQuiz/QuestionDisplay'
import EventTimer from '../../components/EventQuiz/EventTimer'
import Leaderboard from '../../components/EventQuiz/Leaderboard'
import BonusWheel from '../../components/EventQuiz/BonusWheel'
import PodiumScreen from '../../components/EventQuiz/PodiumScreen'

const MAUVE_BG = 'linear-gradient(180deg, #0d001a 0%, #1a0035 60%, #2a0050 100%)'
const CARD_BG = 'rgba(157,78,221,0.1)'
const BORDER = 'rgba(157,78,221,0.3)'
const ACCENT = '#c77dff'

export default function EventQuizPlay() {
  const { sessionId } = useParams()
  const location = useLocation()
  const navigate = useNavigate()

  const savedParticipant = JSON.parse(localStorage.getItem(`participant-${sessionId}`) || 'null')
  const participant = location.state?.participant || savedParticipant

  const [phase, setPhase] = useState('waiting')
  const [currentQuestion, setCurrentQuestion] = useState(null)
  const [timerRemaining, setTimerRemaining] = useState(0)
  const [answered, setAnswered] = useState(null)
  const [answerResult, setAnswerResult] = useState(null)
  const [leaderboard, setLeaderboard] = useState([])
  const [correctAnswer, setCorrectAnswer] = useState(null)
  const [bonusWheelSpinning, setBonusWheelSpinning] = useState(false)
  const [bonusResult, setBonusResult] = useState(null)
  const [countdown, setCountdown] = useState(3)
  const [quizTitle, setQuizTitle] = useState('')
  const [participantCount, setParticipantCount] = useState(0)
  const [totalScore, setTotalScore] = useState(0)
  const questionStartTimeRef = useRef(null)

  const roomCode = participant?.roomCode

  const { connected, state, on, emit } = useEventQuizSocket({
    roomCode,
    role: 'participant',
    participantId: participant?.participantId,
    enabled: !!roomCode && !!participant
  })

  useEffect(() => {
    if (!participant) navigate('/event-quiz/join')
  }, [participant, navigate])

  useEffect(() => {
    if (state) {
      setPhase(state.session?.status || 'waiting')
      setLeaderboard(state.session?.leaderboard || [])
      setParticipantCount(state.session?.participantCount || 0)
      setTimerRemaining(state.session?.timerRemaining || 0)
      setQuizTitle(state.quiz?.title || '')
      if (state.currentQuestion) setCurrentQuestion(state.currentQuestion)
    }
  }, [state])

  useEffect(() => {
    on('event-participant-update', ({ participantCount: c, leaderboard: lb }) => {
      setParticipantCount(c); setLeaderboard(lb)
    })
    on('event-countdown', ({ seconds }) => {
      setPhase('countdown'); setCountdown(seconds)
      const iv = setInterval(() => {
        setCountdown(prev => { if (prev <= 1) { clearInterval(iv); return 0 } return prev - 1 })
      }, 1000)
    })
    on('event-question-start', ({ question, timerRemaining: t, isJackpot }) => {
      setCurrentQuestion(question); setTimerRemaining(t)
      setAnswered(null); setAnswerResult(null); setCorrectAnswer(null); setBonusResult(null)
      setPhase('question')
      questionStartTimeRef.current = Date.now()
      if (isJackpot) playSound('jackpot')
    })
    on('event-timer-tick', ({ timerRemaining: t }) => {
      setTimerRemaining(t)
      if (t <= 5 && t > 0) playSound('tick')
    })
    on('event-timer-end', ({ leaderboard: lb }) => { setLeaderboard(lb); setPhase('review') })
    on('event-review', ({ correctAnswer: ca, leaderboard: lb }) => {
      setCorrectAnswer(ca); setLeaderboard(lb); setPhase('review')
    })
    on('event-answer-result', ({ isCorrect, points, totalScore: ts }) => {
      setAnswerResult({ isCorrect, points }); setTotalScore(ts)
      playSound(isCorrect ? 'correct' : undefined)
    })
    on('event-leaderboard', ({ leaderboard: lb }) => setLeaderboard(lb))
    on('event-bonus-wheel', ({ result }) => {
      setPhase('bonus-wheel'); setBonusWheelSpinning(true); setBonusResult(null)
      setTimeout(() => { setBonusResult(result); setBonusWheelSpinning(false) }, 4200)
    })
    on('event-bonus-applied', () => setPhase('review'))
    on('event-finished', ({ leaderboard: lb }) => {
      setLeaderboard(lb); setPhase('finished'); playSound('first-place')
    })
  }, [on])

  const handleAnswer = useCallback((answer) => {
    if (answered) return
    setAnswered(answer)
    const responseTimeMs = questionStartTimeRef.current ? Date.now() - questionStartTimeRef.current : undefined
    emit('event-submit-answer', { participantId: participant?.participantId, answer, responseTimeMs })
  }, [answered, emit, participant?.participantId])

  const myRank = leaderboard.findIndex(e => e.participantId === participant?.participantId) + 1

  if (phase === 'finished') {
    return (
      <Box sx={{ background: MAUVE_BG, minHeight: '100vh' }}>
        <PodiumScreen winners={leaderboard.slice(0, 3)} title="Compétition terminée !" />
        <Box sx={{ textAlign: 'center', pb: 4, color: 'white' }}>
          <Typography variant="h5" gutterBottom>
            Votre score final : <strong style={{ color: '#c77dff' }}>{totalScore} pts</strong>
          </Typography>
          {myRank > 0 && (
            <Chip
              icon={<EmojiEvents />}
              label={`${myRank === 1 ? '🥇 1er' : myRank === 2 ? '🥈 2e' : myRank === 3 ? '🥉 3e' : `${myRank}e`}`}
              sx={{
                background: myRank <= 3 ? 'linear-gradient(90deg,#7b2fff,#f72585)' : CARD_BG,
                color: 'white', fontSize: '1rem', py: 2,
                boxShadow: myRank <= 3 ? '0 0 20px rgba(123,47,255,0.5)' : 'none'
              }}
            />
          )}
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      background: MAUVE_BG,
      display: 'flex', flexDirection: 'column',
      color: 'white'
    }}>
      {/* Barre de statut */}
      <Box sx={{
        px: 2, pt: 2, pb: 1,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        borderBottom: `1px solid ${BORDER}`,
        background: 'rgba(13,0,26,0.6)',
        backdropFilter: 'blur(10px)'
      }}>
        <Box>
          <Typography variant="caption" sx={{ color: 'rgba(199,125,255,0.6)' }}>
            {quizTitle || 'Quiz Compétition'}
          </Typography>
          <Typography variant="body2" fontWeight="bold" sx={{ color: ACCENT }}>
            {participant?.nickname}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Chip
            label={`${totalScore} pts`}
            size="small"
            sx={{
              background: 'linear-gradient(90deg,#7b2fff,#9d4edd)',
              color: 'white', fontWeight: 'bold'
            }}
          />
          {myRank > 0 && (
            <Chip
              icon={<EmojiEvents fontSize="small" />}
              label={`#${myRank}`}
              size="small"
              sx={{
                bgcolor: myRank <= 3 ? 'rgba(255,215,0,0.2)' : CARD_BG,
                color: myRank <= 3 ? '#FFD700' : 'white',
                border: `1px solid ${myRank <= 3 ? 'rgba(255,215,0,0.4)' : BORDER}`
              }}
            />
          )}
          <Box sx={{
            width: 8, height: 8, borderRadius: '50%',
            bgcolor: connected ? '#06d6a0' : '#ef5350',
            boxShadow: connected ? '0 0 6px #06d6a0' : '0 0 6px #ef5350'
          }} />
        </Box>
      </Box>

      {/* Zone principale */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', px: 2, pb: 2 }}>

        {/* Attente */}
        {phase === 'waiting' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ flex: 1 }}>
            <Box sx={{ textAlign: 'center', mt: 8 }}>
              <Avatar sx={{
                width: 80, height: 80, mx: 'auto', mb: 2,
                background: 'linear-gradient(135deg, #7b2fff, #f72585)',
                fontSize: '2rem', boxShadow: '0 0 30px rgba(123,47,255,0.4)'
              }}>
                {participant?.nickname?.charAt(0)?.toUpperCase() || '?'}
              </Avatar>
              <Typography variant="h5" fontWeight="bold" gutterBottom sx={{
                background: 'linear-gradient(90deg, #c77dff, white)',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'
              }}>
                Bienvenue, {participant?.nickname} !
              </Typography>
              <Typography sx={{ color: 'rgba(199,125,255,0.6)', mb: 4 }}>
                {participantCount} joueur(s) · En attente de l'organisateur…
              </Typography>
              <CircularProgress sx={{ color: ACCENT }} />
            </Box>
          </motion.div>
        )}

        {/* Compte à rebours */}
        {phase === 'countdown' && (
          <motion.div
            initial={{ scale: 2, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h1" fontWeight="900" sx={{
                fontSize: '8rem',
                background: 'linear-gradient(135deg, #c77dff, #f72585)',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                filter: 'drop-shadow(0 0 30px rgba(247,37,133,0.5))'
              }}>
                {countdown}
              </Typography>
              <Typography variant="h5" sx={{ color: 'rgba(199,125,255,0.8)' }}>Préparez-vous !</Typography>
            </Box>
          </motion.div>
        )}

        {/* Question */}
        {phase === 'question' && currentQuestion && (
          <Box sx={{ flex: 1, pt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
              <EventTimer seconds={timerRemaining} large />
            </Box>
            <LinearProgress
              variant="determinate"
              value={currentQuestion.timeLimit ? (timerRemaining / currentQuestion.timeLimit) * 100 : 0}
              sx={{
                mb: 2, height: 5, borderRadius: 3,
                bgcolor: 'rgba(157,78,221,0.2)',
                '& .MuiLinearProgress-bar': {
                  background: timerRemaining <= 5
                    ? 'linear-gradient(90deg,#ef5350,#f72585)'
                    : timerRemaining <= 10
                      ? 'linear-gradient(90deg,#ffa726,#f72585)'
                      : 'linear-gradient(90deg,#7b2fff,#c77dff)'
                }
              }}
            />
            <Box sx={{ bgcolor: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: 3, p: 2 }}>
              <QuestionDisplay
                question={currentQuestion}
                onAnswer={handleAnswer}
                answered={answered}
                showResult={false}
              />
            </Box>

            <AnimatePresence>
              {answered && (
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                  <Box sx={{ textAlign: 'center', mt: 3 }}>
                    {answerResult ? (
                      <Box>
                        {answerResult.isCorrect
                          ? <CheckCircle sx={{ fontSize: 52, color: '#06d6a0' }} />
                          : <Cancel sx={{ fontSize: 52, color: '#f72585' }} />
                        }
                        <Typography variant="h6" fontWeight="bold"
                          sx={{ color: answerResult.isCorrect ? '#06d6a0' : '#f72585' }}>
                          {answerResult.isCorrect ? `+${answerResult.points} pts` : 'Mauvaise réponse'}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography sx={{ color: 'rgba(199,125,255,0.6)' }}>
                        Réponse envoyée — en attente des autres…
                      </Typography>
                    )}
                  </Box>
                </motion.div>
              )}
            </AnimatePresence>
          </Box>
        )}

        {/* Correction */}
        {phase === 'review' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ flex: 1 }}>
            <Box sx={{ textAlign: 'center', pt: 3, mb: 2 }}>
              {answerResult ? (
                <Box>
                  {answerResult.isCorrect
                    ? <CheckCircle sx={{ fontSize: 60, color: '#06d6a0' }} />
                    : <Cancel sx={{ fontSize: 60, color: '#f72585' }} />
                  }
                  <Typography variant="h5" fontWeight="bold"
                    sx={{ color: answerResult.isCorrect ? '#06d6a0' : '#f72585' }}>
                    {answerResult.isCorrect ? `Correct ! +${answerResult.points} pts` : 'Pas cette fois…'}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="h6" sx={{ color: 'rgba(199,125,255,0.7)' }}>Temps écoulé</Typography>
              )}
              {correctAnswer && (
                <Chip
                  label={`Bonne réponse : ${correctAnswer.text}`}
                  sx={{ mt: 1.5, bgcolor: 'rgba(6,214,160,0.2)', color: '#06d6a0', border: '1px solid rgba(6,214,160,0.4)', fontWeight: 'bold' }}
                />
              )}
            </Box>

            <Divider sx={{ borderColor: BORDER, mb: 2 }} />
            <Typography variant="subtitle2" sx={{ color: 'rgba(199,125,255,0.6)', textAlign: 'center', mb: 1 }}>
              Classement
            </Typography>
            <Box sx={{ bgcolor: CARD_BG, borderRadius: 2, p: 1, border: `1px solid ${BORDER}` }}>
              <Leaderboard entries={leaderboard.slice(0, 5)} compact highlightId={participant?.participantId} />
            </Box>
            <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 1, color: 'rgba(199,125,255,0.4)' }}>
              En attente de la prochaine question…
            </Typography>
          </motion.div>
        )}

        {/* Roue bonus */}
        {phase === 'bonus-wheel' && (
          <Box sx={{ flex: 1, bgcolor: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: 3, p: 2, mt: 2 }}>
            <BonusWheel spinning={bonusWheelSpinning} result={bonusResult} />
          </Box>
        )}
      </Box>

      {/* Signature */}
      <Box sx={{
        py: 1, textAlign: 'center',
        borderTop: `1px solid ${BORDER}`,
        background: 'rgba(13,0,26,0.5)'
      }}>
        <Typography variant="caption" sx={{ color: 'rgba(199,125,255,0.4)' }}>
          Cette plateforme a été fièrement construite par{' '}
          <a href="https://www.nouratech.sn" target="_blank" rel="noopener noreferrer"
            style={{ color: '#c77dff', fontWeight: 'bold', textDecoration: 'none' }}>
            Noura Tech
          </a>
        </Typography>
      </Box>
    </Box>
  )
}
